import { useCallback } from "react";
import { FilterDateRange, FilterDateRangeFieldName } from "../interfaces";
import { FilterInfo } from "@/store/deduction/deductionFilter";
import {
  convertDateRangeToDates,
  DATE_CONSTANTS,
  calculateFromDateByMonths,
  CHECK_TO_DATE_FUTURE_BUFFER_MONTHS,
} from "../utils/dateCalculations";
import { FormInstance } from "@ir/ui";
import { ApprovalFilterInfo } from "@/store/approvals";
import { AnalyticsFilterInfo } from "@/store/analytics";
import { AccountingRemittanceFilterInfo } from "@/store/accounting";

interface UseDateRangeHandlersProps<T extends FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo | AccountingRemittanceFilterInfo> {
  defaultCheckDateRangeInMonths: number;
  filterInfo: T;
  isFilterApplied: boolean;
  isDefaultFilter: (info: T) => boolean;
  form: FormInstance;
  onCheckDateRangeUpdate: (value: FilterDateRange | null) => void;
  onInvoiceDateRangeUpdate: (value: FilterDateRange | null) => void;
}

/**
 * Handles date range preset changes and updates form fields accordingly
 */
export const useDateRangeHandlers = <T extends FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo | AccountingRemittanceFilterInfo>({
  defaultCheckDateRangeInMonths,
  filterInfo,
  isFilterApplied,
  isDefaultFilter,
  form,
  onCheckDateRangeUpdate,
  onInvoiceDateRangeUpdate,
}: UseDateRangeHandlersProps<T>) => {
  const calculateDateRange = useCallback(
    (value: FilterDateRange | null, fieldName: FilterDateRangeFieldName): { fromDate: string | null; toDate: string | null } => {
      if (fieldName === "checkDateRange" && !value) {
        if (!isFilterApplied && isDefaultFilter(filterInfo)) {
          return {
            fromDate: calculateFromDateByMonths(defaultCheckDateRangeInMonths),
            toDate: DATE_CONSTANTS.checkToDateDefault,
          };
        }
        return { fromDate: null, toDate: null };
      }

      if (fieldName === "invoiceDateRange" && !value) {
        return { fromDate: null, toDate: null };
      }

      // Check date presets extend toDate by +1 month so future checks are included
      const toDateOffsetMonths = fieldName === "checkDateRange" ? CHECK_TO_DATE_FUTURE_BUFFER_MONTHS : 0;
      return convertDateRangeToDates(value, defaultCheckDateRangeInMonths, toDateOffsetMonths);
    },
    [defaultCheckDateRangeInMonths, filterInfo, isDefaultFilter, isFilterApplied]
  );

  const handleCheckDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      onCheckDateRangeUpdate(value);
      
      if (!value) {
        form.setFieldsValue({ checkDateRange: null });
        return;
      }

      const { fromDate, toDate } = calculateDateRange(value, "checkDateRange");
      form.setFieldsValue({
        checkFromDate: fromDate,
        checkToDate: toDate,
        checkDateRange: value,
      });
    },
    [calculateDateRange, form, onCheckDateRangeUpdate]
  );

  const handleInvoiceDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      onInvoiceDateRangeUpdate(value);
      
      if (!value) {
        form.setFieldsValue({
          invoiceFromDate: null,
          invoiceToDate: null,
          invoiceDateRange: null,
        });
        return;
      }

      const { fromDate, toDate } = calculateDateRange(value, "invoiceDateRange");
      form.setFieldsValue({
        invoiceFromDate: fromDate,
        invoiceToDate: toDate,
        invoiceDateRange: value,
      });
    },
    [calculateDateRange, form, onInvoiceDateRangeUpdate]
  );

  return {
    handleCheckDateRangeChange,
    handleInvoiceDateRangeChange,
    calculateDateRange,
  };
};

